import { useEffect, useMemo, useState } from 'react'
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Area, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from 'recharts'
import './pullman-demand.css'

// Pullman Demand insight — best-selling day + booking-pace early warning.
// Reads /api/dashboards/pullman/demand (agg.PullmanDemand* + cache.PullmanDemandPace,
// populated on-prem by Refresh-Pullman.ps1). Everything is advisory: the pace
// status is now back-tested (see docs/PULLMAN_DEMAND_BACKTEST.md) and behaves as
// a high-recall watch signal — it catches nearly every service that ends up soft
// but ~1 in 3 flags still fills, so it's a prompt to review a date, never an
// auto-alert or discount. Low-sample segments and one-off formats show "watch".

interface BestDay {
  season_label: string; daypart: string; weekday_num: number; weekday_name: string
  service_count: number; avg_seats: number; avg_fill_pct: number
}
interface CurvePt { days_before: number; cum_fill_pct: number; cum_seats: number }
interface CorridorPt { days_before: number; median: number; lo: number; hi: number }
interface Upcoming {
  shd_rec_no: number; service_date: string | null; service_datetime: string | null
  departure_time: string | null; weekday_num: number; weekday_name: string; daypart: string
  format_label: string | null; capacity: number; current_days_before: number; lead_days: number
  current_fill_pct: number; current_seats: number; benchmark_fill_pct: number | null
  pace_index: number | null; status: 'green' | 'amber' | 'red' | 'watch'
  sample_n: number; has_benchmark: boolean; is_special: boolean
  curve: CurvePt[]; corridor: CorridorPt[]
}
interface Payload {
  has_data: boolean; generated_at: string; best_day: BestDay[]; upcoming: Upcoming[]; note: string
}

const MIN_BESTDAY = 4 // a weekday backed by fewer realised services isn't a trustworthy "best day"
const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const STATUS_COLOR: Record<string, string> = { green: '#1f7a3a', amber: '#c2740c', red: '#b42318', watch: '#6b6b6b' }
const STATUS_LABEL: Record<string, string> = { green: 'On pace', amber: 'Behind', red: 'Well behind', watch: 'Watch manually' }

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
  } catch { return iso }
}

export function PullmanDemandDashboard() {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // 'All' (2026-10-23, Kathryn): one chart with every slot — lunch Mon–Sun plus the
  // Friday and Saturday evening services — instead of a Lunch / Evening toggle.
  const [daypart, setDaypart] = useState<'All' | 'Lunch' | 'Evening'>('All')
  const [season, setSeason] = useState<string>('')
  const [rank, setRank] = useState<'fill' | 'volume'>('fill')
  const [selected, setSelected] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/dashboards/pullman/demand', { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then((d: Payload) => { if (cancelled) return; setData(d); const seasons = [...new Set(d.best_day.map((b) => b.season_label))].sort().reverse(); if (seasons.length) setSeason(seasons[0]) })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const seasons = useMemo(() => (data ? [...new Set(data.best_day.map((b) => b.season_label))].sort().reverse() : []), [data])

  const bestDayChart = useMemo(() => {
    if (!data) return []
    if (daypart === 'All') {
      // Every slot that had a service this season: lunch Mon–Sun, then evening Mon–Sun.
      const rows = data.best_day.filter((b) => b.season_label === season)
      const out: { weekday: string; fill: number; seats: number; n: number }[] = []
      for (const dp of ['Lunch', 'Evening']) for (const wd of [1, 2, 3, 4, 5, 6, 7]) {
        const r = rows.find((x) => x.daypart === dp && x.weekday_num === wd)
        if (r && r.service_count > 0) out.push({ weekday: dp === 'Evening' ? `${WD[wd]} eve` : WD[wd], fill: Math.round(r.avg_fill_pct), seats: Math.round(r.avg_seats), n: r.service_count })
      }
      return out
    }
    const rows = data.best_day.filter((b) => b.season_label === season && b.daypart === daypart)
    return [1, 2, 3, 4, 5, 6, 7].map((wd) => {
      const r = rows.find((x) => x.weekday_num === wd)
      return { weekday: WD[wd], fill: r ? Math.round(r.avg_fill_pct) : 0, seats: r ? Math.round(r.avg_seats) : 0, n: r?.service_count ?? 0 }
    })
  }, [data, season, daypart])

  const bestIdx = useMemo(() => {
    const key = rank === 'fill' ? 'fill' : 'seats'
    let bi = -1, bv = -1
    // Only rank days with enough realised services — don't crown a 1-service weekday.
    bestDayChart.forEach((d, i) => { const v = (d as never)[key] as number; if (d.n >= MIN_BESTDAY && v > bv) { bv = v; bi = i } })
    return bi
  }, [bestDayChart, rank])

  const upcoming = useMemo(() => (data ? data.upcoming.filter((u) => daypart === 'All' || u.daypart === daypart) : []), [data, daypart])
  // Findings (2026-10-23, Chris: the chart sat at the bottom and people missed it): the
  // status counts, and the most urgent flagged service pre-selected so a curve shows at once.
  const findings = useMemo(() => {
    const c = { red: 0, amber: 0, green: 0, watch: 0 }
    for (const u of upcoming) c[u.status] += 1
    return c
  }, [upcoming])
  const autoSel = useMemo(() => {
    // The soonest flagged service within two months (far-out services that are not yet on
    // sale read "well behind" at pace 0.00 and would otherwise always win); else the soonest.
    const byDate = [...upcoming].sort((a, b) => (a.service_date ?? '').localeCompare(b.service_date ?? ''))
    const flagged = byDate.find((u) => (u.status === 'red' || u.status === 'amber') && u.lead_days <= 60 && u.current_seats > 0)
    return (flagged ?? byDate[0])?.shd_rec_no ?? null
  }, [upcoming])
  const effectiveSelected = selected ?? autoSel
  const sel = useMemo(() => upcoming.find((u) => u.shd_rec_no === effectiveSelected) ?? null, [upcoming, effectiveSelected])

  const corridorData = useMemo(() => {
    if (!sel) return []
    const byDb = new Map<number, { days_before: number; band?: [number, number]; median?: number; service?: number }>()
    for (const c of sel.corridor) byDb.set(c.days_before, { days_before: c.days_before, band: [c.lo, c.hi], median: c.median })
    for (const p of sel.curve) {
      const row = byDb.get(p.days_before) ?? { days_before: p.days_before }
      row.service = p.cum_fill_pct
      byDb.set(p.days_before, row)
    }
    return [...byDb.values()].sort((a, b) => b.days_before - a.days_before)
  }, [sel])

  if (loading) return <main className="app-main"><p>Loading demand insight…</p></main>
  if (error) return <main className="app-main"><p style={{ color: '#a33' }}>Error: {error}</p></main>
  if (!data) return null

  return (
    <main className="app-main pld">
      <div className="pld-head">
        <div>
          <p className="pld-sub">Best-selling day &amp; booking-pace early warning</p>
        </div>
        <div className="pld-controls">
          <div className="pld-seg">
            <button className={daypart === 'All' ? 'on' : ''} onClick={() => setDaypart('All')}>All services</button>
            <button className={daypart === 'Lunch' ? 'on' : ''} onClick={() => setDaypart('Lunch')}>Lunch</button>
            <button className={daypart === 'Evening' ? 'on' : ''} onClick={() => setDaypart('Evening')}>Evening</button>
          </div>
          {seasons.length > 0 && (
            <select value={season} onChange={(e) => setSeason(e.target.value)} className="pld-select">
              {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="pld-note">{data.note}</div>

      {!data.has_data && (
        <div className="pld-empty">
          Demand data hasn’t been populated yet. It appears after the next <b>Refresh-Pullman.ps1</b> run on ONTRACK
          (which computes the booking curves from MERAC). Ask Chris to run the Pullman refresh, then reload.
        </div>
      )}

      {/* ---------- Best-selling day ---------- */}
      <section className="pld-card">
        <div className="pld-card-head">
          <h3>Best-selling day — {season} · {daypart === 'All' ? 'lunch and evening services' : daypart}</h3>
          <div className="pld-seg pld-seg--sm">
            <button className={rank === 'fill' ? 'on' : ''} onClick={() => setRank('fill')}>By fill %</button>
            <button className={rank === 'volume' ? 'on' : ''} onClick={() => setRank('volume')}>By covers</button>
          </div>
        </div>
        <p className="pld-hint">Biggest-selling and fullest days differ — that’s why both are shown. Bars ranked by {rank === 'fill' ? 'average fill %' : 'average covers'}; the leader is highlighted. Faded bars have too few services (&lt;{MIN_BESTDAY}) to rank reliably.{daypart === 'All' ? ' “eve” bars are the Friday and Saturday evening services (departing 18:20), shown next to the lunch services.' : ''}</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={bestDayChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="weekday" />
            <YAxis yAxisId="l" tickFormatter={(v) => (rank === 'fill' ? `${v}%` : `${v}`)} />
            <Tooltip formatter={(v: number, _n: string, p: { payload?: { n?: number } }) => [rank === 'fill' ? `${v}%` : `${v}`, `${rank === 'fill' ? 'Avg fill' : 'Avg covers'} · ${p?.payload?.n ?? 0} service${(p?.payload?.n ?? 0) === 1 ? '' : 's'}`]} />
            <Bar yAxisId="l" dataKey={rank === 'fill' ? 'fill' : 'seats'} radius={[4, 4, 0, 0]}>
              {bestDayChart.map((d, i) => <Cell key={i} fill={i === bestIdx ? '#2f5d3f' : d.n < MIN_BESTDAY ? '#e2ded2' : '#b7c9bd'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {/* ---------- Upcoming services pace ---------- */}
      <section className="pld-card">
        <div className="pld-card-head"><h3>Upcoming services — booking pace{daypart === 'All' ? '' : ` (${daypart})`}</h3></div>
        <p className="pld-hint"><b>What the pace number means.</b> Pace 0.80 = this service has sold 80% of what a typical service in its slot (same daypart and weekday, 2023–25) had sold by this many days before departure. It is <i>not</i> &ldquo;80% full&rdquo; and <i>not</i> last year&rsquo;s figure. Below 0.70 we flag it <b>Behind</b>; below 0.55 <b>Well behind</b>.</p>
        <p className="pld-hint">Each service&rsquo;s bookings-to-date vs its &ldquo;normal corridor&rdquo; for that daypart &amp; day — a prompt to <i>look</i>, not to discount. Back-testing shows this catches nearly every service that ends up soft, but on a slow season roughly 1 in 3 flagged dates still fill, so use judgement. Once-a-year and low-history dates show &ldquo;watch&rdquo; and are never flagged.</p>
        {/* Findings and the selected service's curve come FIRST (fixed area); the list to pick from sits below. */}
        {upcoming.length > 0 && (
          <div className="pld-findings" role="status">
            <span className="pld-finding" style={{ color: STATUS_COLOR.red }}><b>{findings.red}</b> well behind</span>
            <span className="pld-finding" style={{ color: STATUS_COLOR.amber }}><b>{findings.amber}</b> behind</span>
            <span className="pld-finding" style={{ color: STATUS_COLOR.green }}><b>{findings.green}</b> on pace</span>
            <span className="pld-finding" style={{ color: STATUS_COLOR.watch }}><b>{findings.watch}</b> watch manually</span>
            <span className="pld-finding pld-finding--n">{upcoming.length} upcoming service{upcoming.length === 1 ? '' : 's'}</span>
          </div>
        )}
        {sel ? (
          <div className="pld-corridor">
            <div className="pld-corridor-head">
              <b>{fmtDate(sel.service_date)} · {sel.departure_time} · {sel.format_label}</b>
              {sel.has_benchmark ? (
                <span>Normal corridor from {sel.sample_n} comparable {WD[sel.weekday_num]} {sel.daypart.toLowerCase()} services · shaded = usual range, dashed = typical, solid = this service · <b style={{ color: STATUS_COLOR[sel.status] }}>{STATUS_LABEL[sel.status]}</b>{sel.pace_index != null && <> · pace {sel.pace_index.toFixed(2)}</>}</span>
              ) : (
                <span className="pld-watch">Too few comparable services ({sel.sample_n}){sel.is_special ? ' / one-off format' : ''} — corridor is indicative only, judge by hand</span>
              )}
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={corridorData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="days_before" reversed type="number" domain={['dataMax', 0]} tickFormatter={(v) => `${v}d`} label={{ value: 'days before departure', position: 'insideBottom', offset: -2, fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                <Tooltip
                  formatter={(v: number | number[], n: string) => {
                    if (n === 'band' || Array.isArray(v)) { const [lo, hi] = v as number[]; return [`${Math.round(lo)}–${Math.round(hi)}%`, 'Usual range'] }
                    return [`${Math.round(v as number)}%`, n === 'service' ? 'This service' : 'Typical']
                  }}
                  labelFormatter={(l) => `${l} days before`} />
                <Area dataKey="band" stroke="none" fill="#2f5d3f" fillOpacity={0.12} />
                <Line dataKey="median" stroke="#8a8f9b" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                <Line dataKey="service" stroke={STATUS_COLOR[sel.status]} strokeWidth={2.5} dot={false} connectNulls />
                <ReferenceLine x={sel.current_days_before} stroke="#1a1a1a" strokeDasharray="2 2" label={{ value: 'now', fontSize: 10, position: 'top' }} />
              </ComposedChart>
            </ResponsiveContainer>
            <p className="pld-hint">Pick another service in the list below to see its curve.</p>
          </div>
        ) : (
          <div className="pld-corridor pld-corridor--empty">
            {upcoming.length === 0 ? `No upcoming ${daypart === 'All' ? '' : daypart.toLowerCase() + ' '}services with pace data.` : 'Pick a service in the list below to see its booking curve against the normal corridor.'}
          </div>
        )}
        <h4 className="pld-list-title">Upcoming services — pick one</h4>
        {upcoming.length === 0 ? null : (
          <div className="pld-svc-list">
            {upcoming.map((u) => (
              <button key={u.shd_rec_no} className={`pld-svc ${effectiveSelected === u.shd_rec_no ? 'sel' : ''}`} onClick={() => setSelected(u.shd_rec_no)}>
                <span className="pld-dot" style={{ background: STATUS_COLOR[u.status] }} />
                <span className="pld-svc-date">{fmtDate(u.service_date)}<small>{u.departure_time}</small></span>
                <span className="pld-svc-fmt">{u.format_label}{daypart === 'All' && u.daypart === 'Evening' ? ' · evening' : ''}</span>
                <span className="pld-svc-fill">{Math.round(u.current_fill_pct)}%<small>{u.current_seats}/{u.capacity}</small></span>
                <span className="pld-svc-status" style={{ color: STATUS_COLOR[u.status] }}>
                  {STATUS_LABEL[u.status]}
                  {u.pace_index != null && u.has_benchmark && <small title={`Sold ${Math.round(u.pace_index * 100)}% of what a typical ${u.weekday_name} ${u.daypart.toLowerCase()} service had sold ${u.lead_days} days out`}> · pace {u.pace_index.toFixed(2)}</small>}
                </span>
                <span className="pld-svc-lead">{u.lead_days}d out</span>
              </button>
            ))}
          </div>
        )}

      </section>
    </main>
  )
}
